import { createColumnHelper } from '@tanstack/react-table';
import { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, IconSvg, Text, Tooltip } from '@/lib/v2/components';
import { ReportIcon as ReportIconSolid, ViewsIcon as ViewsIconSolid } from '@/lib/v2/icons/solid';

import { ETriggerActivityType } from '@/src/infrastructure/interfaces/IAutomation.interface';
import { ActionRowMenu } from '@/src/modules/AutomationModuleNew/components';
import { AUTOMATION_PATHS } from '@/src/modules/AutomationModuleNew/constants';
import { RulesIcon } from '@/src/modules/AutomationModuleNew/icons';
import {
  EJourneyMessageState,
  RulesTable,
} from '@/src/modules/AutomationModuleNew/interfaces/automation';

import { useHandleRowActions } from './useHandleRowActions';
import { useTranslationsTypesRules } from './useTranslationsTypesRules';

import { DateFormat, formatDate } from '@/modules/CampaignsModule/utils';

export const useRulesTableColumns = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<RulesTable>();
  const { data: actionRowMenuData, handleRowMenuClick } = useHandleRowActions<RulesTable>();
  const { rulesTypesTrans } = useTranslationsTypesRules();

  const handleGoDetail = useCallback(
    (event: MouseEvent<HTMLButtonElement>, rowData: RulesTable) => {
      event.stopPropagation();
      const { campaignId, id } = rowData;
      navigate(
        `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${campaignId}/action/${id}/email-trigger/${AUTOMATION_PATHS.PREVIEW}`
      );
    },
    [navigate]
  );

  const handleClickReport = useCallback(
    (event: MouseEvent<HTMLButtonElement>, rowData: RulesTable) => {
      event.stopPropagation();
      const { campaignId, id } = rowData;
      navigate(
        `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.RULES_PATH}/${campaignId}/action/${id}/${AUTOMATION_PATHS.REPORT_PATH}`
      );
    },
    [navigate]
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: t('useRulesTableColumns.headerTable.name') as unknown as string,
        cell: (info) => {
          const isActive = info.row.original.status === EJourneyMessageState.ACTIVE;
          return (
            <div className="flex w-full flex-row items-center gap-4">
              <IconSvg
                rounded
                bgColor={isActive ? 'success_new' : 'inactive'}
                fillColor={isActive ? 'success_new' : 'inactive'}
                height="auto"
                svgComponent={<RulesIcon />}
                width="40px"
                withPadding="small"
              />
              <div className="flex-1">
                <Tooltip
                  isClickable
                  noArrow
                  alignment="start"
                  content={info.getValue()}
                  position="top"
                >
                  <Text fontWeight="medium" variant="text">
                    {info.getValue()}
                  </Text>
                </Tooltip>
              </div>
            </div>
          );
        },
        meta: {
          widthClassName: 'w-[30%]',
          enableOrderColumn: true,
        },
      }),
      columnHelper.accessor('ruleType', {
        header: t('useRulesTableColumns.headerTable.dispatch') as unknown as string,
        meta: {
          widthClassName: 'w-[15%]',
        },
        cell: (info) => {
          return rulesTypesTrans[info.getValue() as ETriggerActivityType] || '-';
        },
      }),
      columnHelper.accessor('creationDate', {
        header: t('useRulesTableColumns.headerTable.createDate') as unknown as string,
        meta: {
          widthClassName: 'w-[15%]',
          enableOrderColumn: true,
        },
        cell: (info) => {
          const formattedDate = info.getValue()
            ? formatDate(info.getValue(), DateFormat.LongDateOutSeconds)
            : '-';
          return formattedDate;
        },
      }),
      columnHelper.accessor('exeDate', {
        header: t('useRulesTableColumns.headerTable.execution') as unknown as string,
        meta: {
          widthClassName: 'w-[15%]',
          enableOrderColumn: true,
        },
        cell: (info) => {
          const formattedDate = info.getValue()
            ? formatDate(info.getValue() ?? '', DateFormat.LongDateOutSeconds)
            : '-';
          return formattedDate;
        },
      }),
      columnHelper.accessor('audience', {
        header: t('useRulesTableColumns.headerTable.audience') as unknown as string,
        meta: {
          widthClassName: 'w-[15%]',
          enableOrderColumn: true,
        },
        cell: (info) => {
          return info.getValue() ?? 0;
        },
      }),
      columnHelper.display({
        id: 'action-buttons',
        header: '',
        meta: {
          widthClassName: 'w-[10%]',
        },
        cell: (info) => {
          return (
            <div className="flex items-center gap-1">
              <Button
                gray
                onlyIcon
                outline
                id="preview-actions"
                size="small"
                title={'preview action'}
                onClick={(event) => handleGoDetail(event, info.row.original)}
              >
                <IconSvg fillColor="gray-dark" svgComponent={<ViewsIconSolid />} />
              </Button>
              <Button
                gray
                onlyIcon
                outline
                disabled={info.row.original.audience === 0 || info.row.original.audience === null}
                id="report-actions"
                size="small"
                onClick={(event) => handleClickReport(event, info.row.original)}
              >
                <IconSvg
                  fillColor={
                    info.row.original.audience === 0 || info.row.original.audience === null
                      ? 'disabled'
                      : 'gray-dark'
                  }
                  svgComponent={<ReportIconSolid />}
                />
              </Button>
              <ActionRowMenu
                hideItems={['move']}
                id="options-actions-dropdown"
                onClick={handleRowMenuClick(info.row.original)}
              />
            </div>
          );
        },
      }),
    ];
  }, [columnHelper, t, rulesTypesTrans, handleRowMenuClick, handleGoDetail, handleClickReport]);

  return {
    columns,
    actionRowMenuData: actionRowMenuData ? [actionRowMenuData] : undefined,
  };
};
